import { FC, ReactNode, useRef, useState } from 'react';
import { CheckIcon, CopyIcon } from '@phosphor-icons/react';
import { IconButton } from '@/components/ui/icon-button';
import { PageTitle } from '@/components/ui/text';
import { useAuthUser } from '@/hooks/useAuthUser';
import { desktopOnlyCopy } from './desktopOnlyCopy';

const StepNumber: FC<{ children: ReactNode }> = ({ children }) => (
  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-lureo-mint text-lureo-14 font-semibold text-lureo-emphasis-strong">{children}</span>
);

/** The full-screen hand-off a phone shows where Lureo needs a computer: the address to open there, and who to sign in as. */
export const DesktopHandoff: FC<{ mark: ReactNode; title: string; lead?: string; action: ReactNode }> = ({ mark, title, lead, action }) => {
  const { user } = useAuthUser();
  const [copied, setCopied] = useState(false);
  const address = useRef<HTMLSpanElement>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`https://${desktopOnlyCopy.site}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      if (address.current) window.getSelection()?.selectAllChildren(address.current);
    }
  };

  return (
    <div className="fixed inset-0 z-lureo-layer-blocker flex select-none flex-col items-center overflow-y-auto bg-lureo-emphasis-strong px-6 py-12 text-lureo-on-emphasis">
      <div className="my-auto flex w-full max-w-sm flex-col items-center gap-10">
        {mark}
        <div className="space-y-3 text-center">
          <PageTitle tone="emphasis">{title}</PageTitle>
          {lead && <p className="text-lureo-16 leading-relaxed text-lureo-on-emphasis-subtle">{lead}</p>}
        </div>
        <ol className="w-full space-y-6">
          <li className="flex gap-4">
            <StepNumber>1</StepNumber>
            <div className="min-w-0 flex-1 space-y-2.5 pt-0.5">
              <p className="text-lureo-16 leading-snug">{desktopOnlyCopy.open}</p>
              <div className="flex items-center gap-2 rounded-lureo-radius-pill bg-lureo-on-emphasis/10 py-1.5 pr-1.5 pl-4 ring-1 ring-lureo-on-emphasis/20 ring-inset">
                <span ref={address} className="min-w-0 flex-1 truncate font-lureo-font-mono text-lureo-18 font-medium text-lureo-mint select-text">{desktopOnlyCopy.site}</span>
                <IconButton place="hero" label={copied ? 'Copied' : 'Copy link'} onClick={copy}>
                  {copied ? <CheckIcon className="h-4 w-4" /> : <CopyIcon className="h-4 w-4" />}
                </IconButton>
              </div>
            </div>
          </li>
          <li className="flex gap-4">
            <StepNumber>2</StepNumber>
            <p className="min-w-0 flex-1 pt-0.5 text-lureo-16 leading-snug">
              {desktopOnlyCopy.signIn}
              {user?.email ? <> as <span className="font-semibold break-all">{user.email}</span></> : ' with this account'}
            </p>
          </li>
        </ol>
        {action}
      </div>
    </div>
  );
};
